import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getCategoryExhibition, getCategoryExhibitionCategories } from '../api/home'
import type { CategoryExhibitionItem, HomeCategoryOption } from '../types/home'

type CategoryExhibitionStatus = 'idle' | 'loading' | 'success' | 'error'

type CategoryExhibitionCacheEntry = {
  items: CategoryExhibitionItem[]
  fetchedAt: number
  fallback: boolean
}

interface HomeCategoryExhibitionState {
  selectedCategory: string
  selectedLabel: string
  searchKeyword: string
  status: CategoryExhibitionStatus
  loadingMore: boolean
  pageNum: number
  pageSize: number
  total: number
  hasMore: boolean
  items: CategoryExhibitionItem[]
  categories: HomeCategoryOption[]
  fallback: boolean
  errorMessage: string
  cacheByCategory: Record<string, CategoryExhibitionCacheEntry>
  setSelectedCategory: (category: string) => void
  setSearchKeyword: (keyword: string) => void
  fetchCategories: (query?: string) => Promise<void>
  fetchCategoryExhibition: (category?: string, options?: { force?: boolean; append?: boolean; pageNum?: number }) => Promise<void>
  resetCategoryExhibition: () => void
}

const DEFAULT_CATEGORY = 'smart'
const CACHE_TTL_MS = 5 * 60 * 1000

const mergeCategoryOptions = (current: HomeCategoryOption[], incoming: HomeCategoryOption[], selectedCategory: string, selectedLabel: string) => {
  const merged = new Map<string, HomeCategoryOption>()
  for (const item of [...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])]) {
    if (!item?.value) continue
    merged.set(item.value, item)
  }
  if (selectedCategory && !merged.has(selectedCategory)) {
    merged.set(selectedCategory, {
      key: selectedCategory,
      value: selectedCategory,
      label: selectedLabel || selectedCategory,
      shortLabel: selectedLabel || selectedCategory,
    })
  }
  return [...merged.values()]
}

export const useHomeCategoryExhibitionStore = create<HomeCategoryExhibitionState>()(
  persist(
    (set, get) => ({
      selectedCategory: DEFAULT_CATEGORY,
      selectedLabel: '为你推荐',
      searchKeyword: '',
      status: 'idle',
      loadingMore: false,
      pageNum: 1,
      pageSize: 12,
      total: 0,
      hasMore: false,
      items: [],
      categories: [{ key: DEFAULT_CATEGORY, value: DEFAULT_CATEGORY, label: '为你推荐' }],
      fallback: false,
      errorMessage: '',
      cacheByCategory: {},
      setSelectedCategory: (category) => {
        set({ selectedCategory: String(category || DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY })
      },
      setSearchKeyword: (keyword) => {
        set({ searchKeyword: String(keyword || '').trim() })
      },
      fetchCategories: async (query) => {
        try {
          const categories = await getCategoryExhibitionCategories(String(query || '').trim())
          const next = Array.isArray(categories) && categories.length > 0
            ? categories
            : [{ key: DEFAULT_CATEGORY, value: DEFAULT_CATEGORY, label: '为你推荐' }]
          set({ categories: next })
        } catch {
          set({ categories: [{ key: DEFAULT_CATEGORY, value: DEFAULT_CATEGORY, label: '为你推荐' }] })
        }
      },
      fetchCategoryExhibition: async (category, options) => {
        const targetCategory = String(category || get().selectedCategory || DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY
        const append = options?.append === true
        const targetPageNum = Math.max(1, Number(options?.pageNum || (append ? get().pageNum + 1 : 1)))
        const pageSize = Math.max(1, Number(get().pageSize || 12))
        const now = Date.now()
        const cacheKey = `${targetCategory}:${targetPageNum}`
        const cached = get().cacheByCategory[cacheKey]
        if (!options?.force && cached && (now - cached.fetchedAt) < CACHE_TTL_MS) {
          set({
            selectedCategory: targetCategory,
            selectedLabel: get().categories.find((item) => item.value === targetCategory)?.shortLabel || get().categories.find((item) => item.value === targetCategory)?.label || (targetCategory === DEFAULT_CATEGORY ? '为你推荐' : targetCategory),
            items: append ? [...get().items, ...cached.items] : cached.items,
            fallback: cached.fallback,
            status: 'success',
            loadingMore: false,
            pageNum: targetPageNum,
            errorMessage: '',
          })
          return
        }
        set({
          selectedCategory: targetCategory,
          status: append ? get().status : 'loading',
          loadingMore: append,
          pageNum: append ? get().pageNum : 1,
          errorMessage: '',
        })
        try {
          const data = await getCategoryExhibition({
            category: targetCategory === DEFAULT_CATEGORY ? '' : targetCategory,
            pageNum: targetPageNum,
            pageSize,
          })
          const items = Array.isArray(data.items) ? data.items : []
          set((state) => ({
            selectedCategory: String(data.selected_category || targetCategory).trim() || DEFAULT_CATEGORY,
            selectedLabel: String(data.selected_label || '').trim() || (targetCategory === DEFAULT_CATEGORY ? '为你推荐' : targetCategory),
            items: append ? [...state.items, ...items] : items,
            categories: mergeCategoryOptions(
              state.categories,
              Array.isArray(data.options) && data.options.length > 0 ? data.options : [],
              String(data.selected_category || targetCategory).trim() || DEFAULT_CATEGORY,
              String(data.selected_label || '').trim() || (targetCategory === DEFAULT_CATEGORY ? '为你推荐' : targetCategory)
            ),
            fallback: Boolean(data.fallback),
            status: 'success',
            loadingMore: false,
            pageNum: Number(data.page_num || targetPageNum),
            pageSize: Number(data.page_size || pageSize),
            total: Number(data.total || 0),
            hasMore: Boolean(data.has_more),
            errorMessage: '',
            cacheByCategory: {
              ...state.cacheByCategory,
              [cacheKey]: {
                items,
                fetchedAt: now,
                fallback: Boolean(data.fallback),
              },
            },
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : '网络异常，请稍后重试'
          set({
            status: 'error',
            loadingMore: false,
            errorMessage: message || '网络异常，请稍后重试',
            items: [],
            fallback: true,
          })
        }
      },
      resetCategoryExhibition: () => {
        set({
          selectedCategory: DEFAULT_CATEGORY,
          selectedLabel: '为你推荐',
          searchKeyword: '',
          status: 'idle',
          loadingMore: false,
          pageNum: 1,
          pageSize: 12,
          total: 0,
          hasMore: false,
          items: [],
          fallback: false,
          errorMessage: '',
          cacheByCategory: {},
        })
      },
    }),
    {
      name: 'home-category-exhibition-storage',
      partialize: (state) => ({
        selectedCategory: state.selectedCategory,
      }),
    }
  )
)
